import React, { useCallback, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import styled, { useTheme } from 'styled-components'
import { SingleSelect, Tag } from 'examkrackers-components'
import qs from 'qs'
import * as R from 'ramda'

import { ReactComponent as ArrowDownIcon } from 'assets/arrowDown.svg'
import { ReactComponent as ArrowUpIcon } from 'assets/arrowUp.svg'
import { isNilOrEmpty } from 'utils/ramda'

import { QUIZ_DIAGNOSTICS_RESPONSIVE_MAX_WIDTH } from '../utils/quizDiagnosticsBreakpoints'
import {
  ChapterFilterOption,
  getChapterTagLabel,
  getSafeChapterFilterValue
} from '../utils/filterQuizDiagnosticsData'
import PATHS from 'utils/paths'

import { QUIZ_DIAGNOSTICS_PARAMS } from '../utils/quizDiagnosticsParams'
import { QUIZ_DIAGNOSTICS_OVERLAY_Z_INDEX } from '../utils/quizDiagnosticsOverlayZIndex'

const ALL_CHAPTERS_VALUE = 'all'
const CHAPTER_TAG_CHIP_WIDTH = 56

const FILTER_OPEN_BACKGROUND = '#4A4A4B'
const FILTER_OPEN_BORDER_COLOR = '#DDDFE1'
const FILTER_ACCENT_COLOR = '#F7961B'
const FILTER_DEFAULT_TEXT_COLOR = '#444444'
const FILTER_ARROW_CLOSED_COLOR = '#444444'
const FILTER_ARROW_OPEN_COLOR = '#F7961B'
const FILTER_DROPDOWN_Z_INDEX = QUIZ_DIAGNOSTICS_OVERLAY_Z_INDEX

type QuizDiagnosticsChapterFilterProps = {
  filterOptions: ChapterFilterOption[]
}

const QuizDiagnosticsChapterFilter = ({
  filterOptions
}: QuizDiagnosticsChapterFilterProps): JSX.Element => {
  const { t } = useTranslation()
  const theme = useTheme()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const {
    replace,
    location: { search }
  } = useHistory()

  const parsedQuery = qs.parse(search, { ignoreQueryPrefix: true })
  const selectedChapterIdFromQuery = R.pathOr(
    ALL_CHAPTERS_VALUE,
    [QUIZ_DIAGNOSTICS_PARAMS.chapterId],
    parsedQuery
  )
  const selectedChapterId = String(selectedChapterIdFromQuery)

  const allChaptersOption = useMemo<ChapterFilterOption>(
    () => ({
      label: t('quizDiagnostics.filter.allChapters'),
      value: ALL_CHAPTERS_VALUE
    }),
    [t]
  )

  const isChapterSelected =
    selectedChapterId !== ALL_CHAPTERS_VALUE &&
    filterOptions.some(option => String(option.value) === selectedChapterId)

  const localizedFilterOptions = useMemo<ChapterFilterOption[]>(
    () =>
      isChapterSelected ? [allChaptersOption, ...filterOptions] : filterOptions,
    [allChaptersOption, filterOptions, isChapterSelected]
  )

  const selectedValue = useMemo(() => {
    if (!isChapterSelected) {
      return allChaptersOption
    }

    return getSafeChapterFilterValue(selectedChapterId, localizedFilterOptions)
  }, [
    allChaptersOption,
    isChapterSelected,
    localizedFilterOptions,
    selectedChapterId
  ])

  const handleChapterChange = (option: ChapterFilterOption | null) => {
    const isAllChapters =
      isNilOrEmpty(option) || option?.value === ALL_CHAPTERS_VALUE

    const chapterFilter = isAllChapters
      ? {}
      : { [QUIZ_DIAGNOSTICS_PARAMS.chapterId]: option?.value }

    const newQuery = {
      ...R.dissoc(QUIZ_DIAGNOSTICS_PARAMS.chapterId, parsedQuery),
      ...chapterFilter
    }

    const nextPath = isAllChapters
      ? PATHS.chapterAverages
      : PATHS.sectionAverages
    const queryString = qs.stringify(newQuery, { encode: false })

    replace(queryString ? `${nextPath}?${queryString}` : nextPath)
  }

  const formatOptionLabel = useCallback((option: ChapterFilterOption) => {
    if (option.value === ALL_CHAPTERS_VALUE) {
      return (
        <OptionLabelCellAll>
          <AllChaptersLabel>{option.label}</AllChaptersLabel>
        </OptionLabelCellAll>
      )
    }

    return (
      <OptionLabelCell>
        <ChapterTagChipWrapper>
          <Tag
            color={option?.tag_colour ?? 'grey'}
            isStatic
            text={getChapterTagLabel(option)}
            uppercase
          />
        </ChapterTagChipWrapper>
      </OptionLabelCell>
    )
  }, [])

  const filterLabel = t('quizDiagnostics.filter.label')

  const selectStyles = useMemo(
    () => ({
      menuPortal: (base: Record<string, unknown>) => ({
        ...base,
        zIndex: FILTER_DROPDOWN_Z_INDEX
      }),
      menu: (base: Record<string, unknown>) => ({
        ...base,
        zIndex: FILTER_DROPDOWN_Z_INDEX,
        width: '130px',
        minWidth: '130px',
        marginTop: '4px',
        borderRadius: '6px',
        overflow: 'hidden',
        boxShadow: theme.shadows.mainShadow,
        padding: '0 0 0 12px',
        paddingTop: 0
      }),
      menuList: (base: Record<string, unknown>) => ({
        ...base,
        padding: 0,
        paddingTop: 0,
        maxHeight: '320px',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px'
      }),
      option: (
        base: Record<string, unknown>,
        state: { isFocused: boolean; isSelected: boolean }
      ) => ({
        ...base,
        padding: 0,
        paddingTop: 0,
        margin: 0,
        marginBottom: 0,
        minHeight: '44px',
        height: '44px',
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        backgroundColor: 'transparent',
        color: 'inherit',
        cursor: 'pointer',
        ':hover': {
          backgroundColor: 'transparent'
        },
        ...(state.isFocused || state.isSelected
          ? {
              backgroundColor: 'transparent'
            }
          : {})
      }),
      indicatorsContainer: () => ({
        display: 'none'
      }),
      indicatorSeparator: () => ({
        display: 'none'
      }),
      dropdownIndicator: () => ({
        display: 'none'
      }),
      valueContainer: (base: Record<string, unknown>) => ({
        ...base,
        padding: 0,
        height: '100%',
        alignItems: 'center',
        overflow: 'hidden'
      }),
      singleValue: (base: Record<string, unknown>) => ({
        ...base,
        color: '#444444',
        margin: 0,
        position: 'static',
        top: 'auto',
        transform: 'none',
        maxWidth: '100%',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center'
      }),
      control: (
        base: Record<string, unknown>,
        state: { isFocused: boolean }
      ) => ({
        ...base,
        minHeight: '32px',
        maxHeight: '32px',
        height: '32px',
        backgroundColor: isMenuOpen ? FILTER_OPEN_BACKGROUND : '#ffffff',
        borderColor: isMenuOpen ? FILTER_OPEN_BORDER_COLOR : '#cccccc',
        boxShadow: 'none',
        padding: '0 32px 0 12px',
        alignItems: 'center',
        overflow: 'hidden',
        '&:hover': {
          borderColor: isMenuOpen ? FILTER_OPEN_BORDER_COLOR : '#cccccc',
          backgroundColor: isMenuOpen ? FILTER_OPEN_BACKGROUND : '#ffffff'
        },
        ...(state.isFocused
          ? {
              borderColor: isMenuOpen ? FILTER_OPEN_BORDER_COLOR : '#cccccc',
              boxShadow: 'none',
              backgroundColor: isMenuOpen ? FILTER_OPEN_BACKGROUND : '#ffffff'
            }
          : {})
      })
    }),
    [isMenuOpen, theme.shadows.mainShadow]
  )

  return (
    <FiltersWrapper $isMenuOpen={isMenuOpen}>
      <FilterLabel htmlFor='quiz-diagnostics-chapter-filter'>
        {filterLabel}
      </FilterLabel>
      <SelectControl $isMenuOpen={isMenuOpen}>
        <StyledSelect
          $isMenuOpen={isMenuOpen}
          $isAllChaptersSelected={!isChapterSelected}
          isClearable={false}
          isSearchable={false}
          removeMargin
          removeTopLabel
          size='small'
          id='quiz-diagnostics-chapter-filter'
          label={filterLabel}
          placeholder={t('quizDiagnostics.filter.placeholder')}
          options={localizedFilterOptions}
          value={selectedValue}
          formatOptionLabel={formatOptionLabel}
          onChange={handleChapterChange}
          menuPortalTarget={document.body}
          menuPosition='fixed'
          styles={selectStyles}
          onMenuOpen={() => setIsMenuOpen(true)}
          onMenuClose={() => setIsMenuOpen(false)}
        />
        <ArrowToggle $isMenuOpen={isMenuOpen} aria-hidden>
          {isMenuOpen ? <ArrowUpIcon /> : <ArrowDownIcon />}
        </ArrowToggle>
      </SelectControl>
    </FiltersWrapper>
  )
}

export default QuizDiagnosticsChapterFilter

const FiltersWrapper = styled.div<{ $isMenuOpen: boolean }>`
  display: flex;
  flex-direction: column;
  width: 130px;
  flex-shrink: 0;
  position: relative;
  z-index: ${({ $isMenuOpen }) =>
    $isMenuOpen ? FILTER_DROPDOWN_Z_INDEX : 'auto'};

  @media (max-width: ${QUIZ_DIAGNOSTICS_RESPONSIVE_MAX_WIDTH}px) {
    width: 100%;
  }
`

const FilterLabel = styled.label`
  display: block;
  margin: 0 0 6px;
  padding: 0;
  font-family: Arial, sans-serif;
  font-size: 11px;
  font-weight: 400;
  line-height: 14px;
  color: #444444;
  white-space: nowrap;
`

const SelectControl = styled.div<{ $isMenuOpen: boolean }>`
  width: 130px;
  height: 32px;
  position: relative;
  z-index: ${({ $isMenuOpen }) =>
    $isMenuOpen ? FILTER_DROPDOWN_Z_INDEX : 'auto'};

  & > div {
    margin: 0 !important;
    width: 100% !important;
  }

  & > div svg {
    display: none !important;
  }

  label {
    display: none !important;
    height: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
    overflow: hidden !important;
  }

  @media (max-width: ${QUIZ_DIAGNOSTICS_RESPONSIVE_MAX_WIDTH}px) {
    width: 100%;
  }
`

const ArrowToggle = styled.span<{ $isMenuOpen: boolean }>`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 10px;
  height: 10px;
  pointer-events: none;
  z-index: 2;

  svg {
    width: 14px;
    height: 14px;
    display: block;

    path {
      fill: ${({ $isMenuOpen }) =>
        $isMenuOpen ? FILTER_ARROW_OPEN_COLOR : FILTER_ARROW_CLOSED_COLOR};
    }
  }
`

const OptionLabelCell = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  height: 23px;
  width: ${CHAPTER_TAG_CHIP_WIDTH}px;
  min-width: ${CHAPTER_TAG_CHIP_WIDTH}px;
  max-width: ${CHAPTER_TAG_CHIP_WIDTH}px;
  margin: 0;
  padding: 0;
  flex-shrink: 0;
`

const OptionLabelCellAll = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  height: 23px;
  margin: 0;
  padding: 0;
`

const AllChaptersLabel = styled.span`
  display: inline-flex;
  align-items: center;
  height: 23px;
  margin: 0;
  padding: 0;
  font-family: Arial, sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #444444;
`

const ChapterTagChipWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${CHAPTER_TAG_CHIP_WIDTH}px;
  min-width: ${CHAPTER_TAG_CHIP_WIDTH}px;
  max-width: ${CHAPTER_TAG_CHIP_WIDTH}px;
  height: 23px;
  margin: 0;
  padding: 0;
  flex-shrink: 0;
  overflow: hidden;
  pointer-events: none;

  & > div {
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    width: ${CHAPTER_TAG_CHIP_WIDTH}px !important;
    min-width: ${CHAPTER_TAG_CHIP_WIDTH}px !important;
    max-width: ${CHAPTER_TAG_CHIP_WIDTH}px !important;
    height: 23px !important;
    min-height: 23px !important;
    padding: 0 4px !important;
    box-sizing: border-box !important;
    border-radius: 2px !important;
    font-family: Arial, sans-serif !important;
    font-size: 10px !important;
    line-height: 23px !important;
    letter-spacing: normal !important;
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    text-transform: uppercase;
    flex-shrink: 0;
    pointer-events: none;
  }
`

const StyledSelect = styled(SingleSelect)<{
  $isMenuOpen: boolean
  $isAllChaptersSelected: boolean
}>`
  width: 130px;
  position: relative;
  z-index: ${({ $isMenuOpen }) =>
    $isMenuOpen ? FILTER_DROPDOWN_Z_INDEX : 'auto'};

  & > div {
    margin: 0 !important;
  }

  div[class*='control'] {
    width: 130px !important;
    min-width: 130px !important;
    min-height: 32px !important;
    max-height: 32px !important;
    height: 32px !important;
    border-radius: 6px !important;
    border: 1px solid
      ${({ $isMenuOpen }) =>
        $isMenuOpen ? FILTER_OPEN_BORDER_COLOR : '#cccccc'} !important;
    background-color: ${({ $isMenuOpen }) =>
      $isMenuOpen ? FILTER_OPEN_BACKGROUND : '#ffffff'} !important;
    box-shadow: none !important;
    padding: 0 32px 0 12px !important;
    align-items: center !important;
    overflow: hidden !important;

    &:hover,
    &:focus-within {
      border-color: ${({ $isMenuOpen }) =>
        $isMenuOpen ? FILTER_OPEN_BORDER_COLOR : '#cccccc'} !important;
      box-shadow: none !important;
      background-color: ${({ $isMenuOpen }) =>
        $isMenuOpen ? FILTER_OPEN_BACKGROUND : '#ffffff'} !important;
    }
  }

  div[class*='IndicatorsContainer'],
  div[class*='indicatorContainer'],
  div[class*='indicatorSeparator'],
  div[class*='DropdownIndicator'],
  div[class*='control'] svg {
    display: none !important;
  }

  div[class*='valueContainer'] {
    padding: 0 !important;
    height: 100% !important;
    align-items: center !important;
    overflow: hidden !important;
    flex-wrap: nowrap !important;
  }

  div[class*='singleValue'] {
    position: static !important;
    top: auto !important;
    transform: none !important;
    margin: 0 !important;
    max-width: 100% !important;
    overflow: hidden !important;
    display: flex !important;
    align-items: center !important;
  }

  div[class*='singleValue'] ${ChapterTagChipWrapper} {
    flex-shrink: 0;
  }

  div[class*='singleValue'] ${AllChaptersLabel} {
    font-family: Arial, sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    color: ${({ $isMenuOpen, $isAllChaptersSelected }) =>
      $isMenuOpen && $isAllChaptersSelected
        ? FILTER_ACCENT_COLOR
        : FILTER_DEFAULT_TEXT_COLOR};
  }

  div[class*='menu'] {
    width: 130px !important;
    min-width: 130px !important;
    border-radius: 6px !important;
    padding: 0 0 0 12px !important;
    padding-top: 0 !important;
    z-index: ${FILTER_DROPDOWN_Z_INDEX} !important;
  }

  div[class*='menuList'] {
    max-height: 320px !important;
    padding: 0 !important;
    padding-top: 0 !important;
    display: flex !important;
    flex-direction: column !important;
    gap: 8px !important;
  }

  div[class*='option'] {
    display: flex !important;
    justify-content: flex-start !important;
    align-items: center !important;
    padding: 0 !important;
    margin: 0 !important;
    margin-bottom: 0 !important;
    min-height: 44px !important;
    height: 44px !important;
    box-sizing: border-box !important;
    background-color: transparent !important;
    cursor: pointer !important;
  }

  div[class*='option'] > div {
    display: flex !important;
    align-items: center !important;
    justify-content: flex-start !important;
    width: 100% !important;
    height: auto !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  div[class*='option'] ${OptionLabelCell} {
    margin: 0 !important;
    padding: 0 !important;
    width: ${CHAPTER_TAG_CHIP_WIDTH}px !important;
    min-width: ${CHAPTER_TAG_CHIP_WIDTH}px !important;
    max-width: ${CHAPTER_TAG_CHIP_WIDTH}px !important;
    flex-shrink: 0 !important;
  }

  div[class*='option'] ${ChapterTagChipWrapper} {
    width: ${CHAPTER_TAG_CHIP_WIDTH}px !important;
    min-width: ${CHAPTER_TAG_CHIP_WIDTH}px !important;
    max-width: ${CHAPTER_TAG_CHIP_WIDTH}px !important;
    flex-shrink: 0 !important;
  }

  @media (max-width: ${QUIZ_DIAGNOSTICS_RESPONSIVE_MAX_WIDTH}px) {
    width: 100%;

    div[class*='control'] {
      width: 100% !important;
      min-width: unset !important;
    }

    div[class*='menu'] {
      width: 100% !important;
      min-width: unset !important;
    }
  }
`
